import _ from 'lodash';
import React from 'react';
import useAppSelector from '../../app/redux/useAppSelector';
import Avatar from '../../components/Avatar';
import Badge from '../../components/Badge';
import { MenuItem } from '../../components/Menu';
import { TopicUserRole, UserType } from '../../graphql/generated/graphql';
import AdminBadge from '../../user/AdminBadge';
import UserBadge from '../../user/profile/UserBadge';
import { Link } from '../../utils/router/v3Bridge';
import userPath from '../../utils/url/userPath';
import topicPolicyCheck, {
  isUserInTopic,
  TopicPolicyAction,
} from '../topicPolicyCheck';
import styles from './TopicUserRow.module.scss';
import TopicUsersPanelRow from './TopicUsersPanelRow';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';

const TOPIC_FRAGMENT = gql(`
  fragment TopicUserRow_topic on Topic {
    ...topicPolicyCheck_topic
  }
`);

const USER_FRAGMENT = gql(`
  fragment TopicUserRow_user on User {
    id
    type
    name
    ...Avatar_user
    ...userPath_user
  }
`);

const USE_DEMOTION_MENU_ITEM_TOPIC_FRAGMENT = gql(`
  fragment useDemotionMenuItem_topic on Topic {
    id
    ...topicPolicyCheck_topic
  }
`);

interface UseMenuItemParameters {
  topic?: DocumentType<typeof USE_DEMOTION_MENU_ITEM_TOPIC_FRAGMENT>;
  user: { id: string };
  userRole: TopicUserRole;
  onRemove?: () => unknown;
  onUpdate?: () => unknown;
}

const useDemotionMenuItem = ({
  topic,
  user,
  userRole,
  onUpdate,
}: UseMenuItemParameters) => {
  const currentUser = useAppSelector((state) => state.session.user);

  if (
    !topic ||
    userRole !== TopicUserRole.Owner ||
    !onUpdate ||
    !isUserInTopic(topic, currentUser)
  ) {
    return undefined;
  }

  const hasPermission = topicPolicyCheck(
    TopicPolicyAction.AddRemoveOwner,
    topic,
    currentUser,
  );

  return (
    hasPermission && (
      <MenuItem key="demote" onClick={() => onUpdate()}>
        {currentUser.id === user.id ? 'Demote yourself' : 'Revoke ownership'}
      </MenuItem>
    )
  );
};

const usePromotionMenuItem = ({
  topic,
  user,
  userRole,
  onUpdate,
}: UseMenuItemParameters) => {
  const currentUser = useAppSelector((state) => state.session.user);
  const isSelf = currentUser.id === user.id;

  if (
    !topic ||
    userRole !== TopicUserRole.Member ||
    !onUpdate ||
    !isUserInTopic(topic, currentUser)
  ) {
    return undefined;
  }

  const hasPermission = topicPolicyCheck(
    TopicPolicyAction.AddRemoveOwner,
    topic,
    currentUser,
  );

  const hasAdminPermission = isSelf && currentUser.type === UserType.Admin;

  return (
    (hasPermission || hasAdminPermission) && (
      <MenuItem
        key="promote"
        onClick={() => onUpdate()}
        right={!hasPermission && hasAdminPermission ? <AdminBadge /> : null}
      >
        {isSelf ? 'Become owner' : 'Make owner'}
      </MenuItem>
    )
  );
};

const useApproveMenuItem = ({
  topic,
  user,
  userRole,
  onUpdate,
}: UseMenuItemParameters) => {
  const currentUser = useAppSelector((state) => state.session.user);

  if (!topic || !onUpdate || userRole !== TopicUserRole.Requestor) {
    return undefined;
  }

  if (currentUser.id === user.id) {
    // Can happen if user requested access to a private topic, which later become public
    const hasPermission = topicPolicyCheck(
      TopicPolicyAction.AddRemoveMember,
      topic,
      currentUser,
    );

    if (hasPermission) {
      return (
        <MenuItem key="approve" onClick={() => onUpdate()}>
          Grand yourself access
        </MenuItem>
      );
    }

    return currentUser.type === UserType.Admin ? (
      <MenuItem key="approve" onClick={() => onUpdate()} right={<AdminBadge />}>
        Approve yourself
      </MenuItem>
    ) : undefined;
  }

  const hasPermission =
    isUserInTopic(topic, currentUser) &&
    topicPolicyCheck(TopicPolicyAction.AddRemoveMember, topic, currentUser);

  return (
    hasPermission && (
      <MenuItem key="approve" onClick={() => onUpdate()}>
        Grant access
      </MenuItem>
    )
  );
};

const useRemoveMenuItem = ({
  topic,
  user,
  userRole,
  onRemove,
}: UseMenuItemParameters) => {
  const currentUser = useAppSelector((state) => state.session.user);
  const isSelf = currentUser.id === user.id;

  if (!topic || !onRemove) {
    return undefined;
  }

  const hasPermission =
    isSelf ||
    (isUserInTopic(topic, currentUser) &&
      topicPolicyCheck(
        userRole === TopicUserRole.Owner
          ? TopicPolicyAction.AddRemoveOwner
          : TopicPolicyAction.AddRemoveMember,
        topic,
        currentUser,
      ));

  return (
    hasPermission && (
      <MenuItem key="remove" danger onClick={() => onRemove()}>
        {isSelf && 'Leave topic'}
        {!isSelf &&
          (userRole === TopicUserRole.Requestor
            ? 'Decline access'
            : 'Remove from topic')}
      </MenuItem>
    )
  );
};

export const useMenuItems = (parameters: UseMenuItemParameters) => {
  const demotionItem = useDemotionMenuItem(parameters);
  const promotionItem = usePromotionMenuItem(parameters);
  const approveItem = useApproveMenuItem(parameters);
  const removeItem = useRemoveMenuItem(parameters);

  return _.compact([demotionItem, promotionItem, approveItem, removeItem]);
};

interface TopicUserRowProps {
  compact?: boolean;
  hideMenu?: boolean;
  link?: boolean;
  onClick?: () => unknown;
  onRemove?: () => unknown;
  onUpdate?: () => unknown;
  topic?: DocumentType<typeof TOPIC_FRAGMENT>;
  user: DocumentType<typeof USER_FRAGMENT>;
  userRole: TopicUserRole;
}

const TopicUserRow = ({
  compact = false,
  hideMenu = false,
  link,
  onClick,
  onRemove,
  onUpdate,
  topic,
  user,
  userRole = TopicUserRole.Member,
}: TopicUserRowProps) => {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const showUserBadge = user.type && ['GUEST', 'BOT'].includes(user.type);

  const menuItems = useMenuItems({
    onRemove,
    onUpdate,
    topic,
    user,
    userRole,
  });

  const content = (
    <div className={styles.name}>
      {user.name}
      {(userRole !== TopicUserRole.Member || showUserBadge) && (
        <div className={styles.badgeContainer}>
          {userRole === TopicUserRole.Owner && <Badge>Owner</Badge>}
          {userRole === TopicUserRole.Requestor && (
            <Badge color="gray">Requested</Badge>
          )}
          {showUserBadge && <UserBadge user={user} />}
        </div>
      )}
    </div>
  );

  return (
    <TopicUsersPanelRow
      compact={compact}
      onClick={onClick}
      icon={<Avatar user={user} link={link} size={compact ? 24 : 'regular'} />}
      content={
        link ? (
          <Link className={styles.link} to={userPath(user)}>
            {content}
          </Link>
        ) : (
          content
        )
      }
      menu={!hideMenu && menuItems.length > 0 ? menuItems : null}
    />
  );
};

export default TopicUserRow;
